import React, { useState, useEffect, useRef } from 'react'
import { Container, Col, Row, ButtonToolbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import Uppy from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import TextAreaInput from 'components/ui/forms/field/text-area/TextArea'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import AwesomeFileInput, {
  FileUpload,
} from 'components/ui/forms/field/awesomeFileInput/AwesomeFileInput'
import { UploadFileStatusEnum } from 'enum/uploadFile-status.enum'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/blocks.action'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import { getS3FileId } from 'helpers/converters.helper'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { gbToBytes } from 'helpers/others.helper'
import { multimediaBlockIframeAllowedFiles } from 'helpers/allowedExtensionFiles.helper'
import styles from './Multimedia.module.scss'
import { useAskCanDownload } from './hooks/useAskCanDownload'
import DenyDownload from './DenyDownload'

interface IMultimedia {
  onCancel: any
  editing: boolean
}

const defaultValues = {
  name: '',
  description: '',
  file: {},
  showTitle: true,
  lockProgress: false,
  denyDownload: false,
}

// Restricciones
const ONE_GB = gbToBytes(1)

const Multimedia: React.FC<IMultimedia> = ({ onCancel, editing }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [data, setData] = useState(defaultValues)
  const [filesStatus, setFileStatus] = useState<FileUpload[]>([])
  const [endedFile, setEndedFile] = useState({} as any)
  const [errorFile, setErrorFile] = useState({} as any)
  const [newChange, setNewChange] = useState(false)
  const [filesArray, setFilesArray] = useState({} as any)

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmModalAction, setConfirmModalAction] = useState()
  // Referencias usadas para mantener la data actualizada en todo el componente
  const ref = useRef({} as any)
  const cancel = useRef('')

  // Se encarga de intercambiar los ids de los archivos generados por uppy por los asignados por el back al crear la respuesta
  const { ids } = useSelector(({ answer }: IMainState) => answer)
  const askCanDownload = useAskCanDownload(filesStatus)

  useEffect(() => {
    if (ids.length > 0) {
      setNewChange(true)
    }
  }, [ids])

  // Previene que el usuario recarge o cambie de página cuando un archivo aun se esté cargando
  const preventPageReload = (event: any) => {
    for (const id in ref.current) {
      if (ref.current[id].status === UploadFileStatusEnum.inProcess) {
        event.preventDefault()
        event.returnValue = true
      }
    }
  }
  useEffect(() => {
    window.addEventListener('beforeunload', preventPageReload)
    return () => {
      window.removeEventListener('beforeunload', preventPageReload)
    }
  }, [])

  // Cargar datos existes
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)
  useEffect(() => {
    if (edit) {
      const { name, description, showTitle, file, lockProgress, denyDownload } =
        edit
      setData({
        ...defaultValues,
        name,
        description,
        showTitle: !!showTitle,
        lockProgress: !!lockProgress,
        denyDownload: !!denyDownload,
      })
      ref.current = {}
      const ids = [] as any
      if (file && file.id) {
        const {
          id,
          metadata: { size, filename, mimeType },
          url,
        } = file
        ref.current[id] = {
          id,
          name: filename,
          type: mimeType,
          size,
          progress: 0,
          status: UploadFileStatusEnum.completed,
          download: url,
        }
        id && ids.push(id)
      }
      setNewChange(true)
    }
  }, [edit])

  // Guardar
  const handleOnSave = () => {
    const { id, lessonId } = edit
    let file: any = null
    const hasFiles = !!filesStatus.length

    let result = {
      ...data,
      id,
      lessonId,
    }
    for (const id in ref.current) {
      const currentFile = ref.current[id]
      if (getS3FileId(currentFile.download)) {
        file = {
          id: getS3FileId(currentFile.download),
          storage: 'cache',
          metadata: {
            uppy_id: id,
            filename: currentFile.name,
            mime_type: currentFile.type,
            size: currentFile.size,
          },
        }
        result = { ...result, file }
      }
    }
    // @ts-ignore
    if (hasFiles && !result.file.id) {
      const loadedFile = filesStatus[0]
      const idFile = loadedFile.id

      file = {
        id: idFile,
        storage: 'store',
        metadata: {
          uppy_id: id,
          filename: loadedFile.name,
          mime_type: loadedFile.type,
          size: loadedFile.size,
        },
      }
      result = { ...result, file }
    }
    dispatch({ type: fromActions.EDIT_MULTIMEDIA_BLOCK, payload: result })
    onCancel()
  }

  const handleContinue = (action: any) => {
    let canContinue = true
    for (const id in ref.current) {
      if (ref.current[id].status === UploadFileStatusEnum.inProcess) {
        canContinue = false
        setShowConfirmationModal(true)
        setConfirmModalAction(() => action)
      }
    }
    canContinue && action()
  }

  // Inicio de la instancia de uppy 💀
  const uppy = Uppy({
    restrictions: {
      maxFileSize: ONE_GB,
    },
  }).use(AwsS3Multipart, {
    companionUrl: `//${process.env.REACT_APP_API_URL}/multimedia`,
  })

  // Valida que el nombre del archivo no se repita
  const isTheSameFile = (filename: string) => {
    for (const id in ref.current) {
      if (ref.current[id].name === filename) return true
    }
    return false
  }

  // Se selecciona un archivo (arrastrar o seleccionar)
  const onSelectFiles = (files: any) => {
    if (files && files.length > 0) {
      files.map((file: any) => {
        if (!isTheSameFile(file.name)) {
          if (file.size < ONE_GB) {
            try {
              uppy.addFile({
                id: '',
                source: 'taskBlock',
                name: file.name,
                type: file.type,
                data: file,
              })
              setFilesArray((current: any) => ({
                ...current,
                [file.name]: file,
              }))
            } catch (err) {
              ErrorToast(
                t(`lessons.contentBlocks.taskBlock.toast.uploadProblems`)
              )
            }
          } else {
            ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.sizeExceeded`))
          }
        } else {
          ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.sameName`))
        }
      })
      uppy.upload()
    }
  }

  // Comienza la carga
  uppy.on('upload', () => {
    const toDelete = Object.keys(ref.current)[0]
    cancel.current = toDelete
    ref.current = {}
  })
  // Actualiza el porcentaje de carga (una instancia de socket por cada archivo)
  uppy.on(
    'upload-progress',
    ({ id, name, size, type }, { bytesUploaded, bytesTotal }) => {
      // @ts-ignore
      const percentage = (bytesUploaded / bytesTotal).toFixed(2) * 100
      if (id !== cancel.current) {
        ref.current[id] = {
          id,
          name,
          size,
          type,
          progress: percentage,
          status: UploadFileStatusEnum.inProcess,
        }
        setNewChange(true)
      }
      if (cancel.current != '') {
        uppy.getFile(cancel.current) && uppy.removeFile(cancel.current)
      }
    }
  )
  // Termina la carga (una instancia de socket por cada archivo)
  uppy.on('upload-success', ({ id, name, size, type }, { uploadURL }) => {
    const result = {} as any
    result[id] = {
      id,
      name,
      size,
      progress: 100,
      type,
      status: UploadFileStatusEnum.completed,
      download: uploadURL,
    }
    setEndedFile(result)
  })

  // Despues de cancelar la carga de un archivo, uppy lanza este evento
  uppy.on('file-removed', ({ id }) => {
    if (cancel.current === id) {
      cancel.current = ''
    }
  })

  // Cuando uppy destecta algún error en la carga (solo probado con errores de conexión)
  uppy.on('upload-error', ({ id, name, size }, error) => {
    const result = {} as any
    result[id] = {
      id,
      name,
      size,
      progress: 0,
      status: UploadFileStatusEnum.error,
      error,
    }
    setErrorFile(result)
  })

  // Si un archivo termina su carga (terminó de cargar o tuvo errores), se actualiza el estado principal
  useEffect(() => {
    if (Object.values(endedFile).length > 0) {
      for (const id in endedFile) {
        if (ref.current[id]) {
          if (!ref.current[id].download) {
            ref.current[id] = endedFile[id]
            setNewChange(true)
          }
        }
      }
    }
    if (Object.values(errorFile).length > 0) {
      for (const id in errorFile) {
        if (ref.current[id] && !ref.current[id].error) {
          ref.current[id] = errorFile[id]
          setNewChange(true)
        }
      }
    }
  }, [endedFile, errorFile])

  // Cada vez que un archivo entra en estado de error, se puede lanzar un retry, que vuelve a cargar el archivo desde cero
  const handleRetry = (name: any, id: string) => {
    try {
      const file = filesArray[name]
      if (file) {
        uppy.addFile({
          source: 'taskBlock',
          name: file.name,
          type: file.type,
          data: file,
        })
        uppy.upload()
        setFilesArray((current: any) => ({ ...current, [file.name]: file }))
        delete ref.current[id]
        setNewChange(true)
      }
    } catch (error) {
      ErrorToast(t(`lessons.contentBlocks.taskBlock.toast.uploadProblems`))
    }
  }

  // Cancelar la subida de un archivo
  const handleCancel = (id: string) => {
    cancel.current = id
    delete ref.current[id]
    setNewChange(true)
  }

  // Si se genera un cambio en el ref principal, este cambio se ve reflejado en el state fileStatus (no se pasa directamente ref.current, ya que las referencias no generan callback)
  useEffect(() => {
    if (newChange) {
      setFileStatus(Object.values(ref.current))
      setNewChange(false)
    }
  }, [newChange])

  return (
    <Container fluid>
      <Row>
        <h1 className={styles.title}>
          {t(
            `administration.blocks.type.multimedia.${editing ? 'edit' : 'new'}`
          )}
        </h1>
      </Row>
      <Row>
        <Col xs={6} className={'p-0 pt-3'}>
          <TextInput
            name={'name'}
            value={data['name'] || ''}
            label={t('administration.blocks.type.multimedia.title')}
            placeHolder={''}
            onChange={(e: any) => setData({ ...data, name: e.target.value })}
            disabled={false}
            error={''}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className={'p-0 pt-3'}>
          <TextAreaInput
            name={'description'}
            rows={3}
            label={t('administration.blocks.type.multimedia.description')}
            value={data['description'] || ''}
            onChange={(e: any) =>
              setData({ ...data, description: e.target.value })
            }
            error={''}
          />
        </Col>
      </Row>
      <Row>
        <InputLabel
          label={
            <>
              {t('administration.blocks.type.multimedia.uploadFile')}
              <CommonToolTip
                text={t(
                  'administration.blocks.type.multimedia.tootltips.uploadFile'
                )}
                position="right"
                className={styles.tabsTooltip}
              >
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className={`ml-2 ${styles.infoIcon}`}
                />
              </CommonToolTip>
            </>
          }
        />
      </Row>
      <Row>
        <Col xs={8} className={`${styles.fileInputContainer} p-0`}>
          <AwesomeFileInput
            id={'1'}
            files={filesStatus}
            sizeLimit={ONE_GB}
            multiFile={false}
            onCancel={handleCancel}
            onRetry={handleRetry}
            onChange={onSelectFiles}
          />
        </Col>
      </Row>
      <Row className="pt-3">
        <div>
          <InputLabel
            label={t('administration.blocks.type.multimedia.showTitle.title')}
          />
          <CheckBoxRadioButtonInput
            name={'mandatory-task-true'}
            value={true}
            checked={data['showTitle']}
            inline={true}
            type={'radio'}
            label={t('administration.blocks.type.multimedia.showTitle.yes')}
            onChange={() => setData({ ...data, showTitle: true })}
            error={''}
          />
          <CheckBoxRadioButtonInput
            name={'mandatory-task-false'}
            value={false}
            checked={!data['showTitle']}
            inline={true}
            type={'radio'}
            label={t('administration.blocks.type.multimedia.showTitle.no')}
            onChange={() => setData({ ...data, showTitle: false })}
            error={''}
          />
        </div>
      </Row>
      <Row className="pt-3">
        <div>
          <InputLabel
            label={t(
              'administration.blocks.type.multimedia.lockProgress.title'
            )}
          />
          <CheckBoxRadioButtonInput
            name={'lockProgress-task-true'}
            value={true}
            checked={data.lockProgress}
            inline={true}
            type={'radio'}
            label={t('administration.blocks.type.multimedia.lockProgress.yes')}
            onChange={() => setData({ ...data, lockProgress: true })}
            error={''}
          />
          <CheckBoxRadioButtonInput
            name={'lockProgress-task-false'}
            value={false}
            checked={!data.lockProgress}
            inline={true}
            type={'radio'}
            label={t('administration.blocks.type.multimedia.lockProgress.no')}
            onChange={() => setData({ ...data, lockProgress: false })}
            error={''}
          />
        </div>
      </Row>
      {askCanDownload && (
        <Row className="pt-3">
          <DenyDownload
            checked={!data.denyDownload}
            onChange={(allowDownload) =>
              setData({ ...data, denyDownload: !allowDownload })
            }
          />
        </Row>
      )}
      <ButtonToolbar className="float-right mt-5">
        <Button
          onClick={() => handleContinue(onCancel)}
          variant={ButtonVariant.Secondary}
          size={ButtonSizes.Small}
        >
          {t('administration.blocks.type.multimedia.buttons.cancel')}
        </Button>
        <Button
          size={ButtonSizes.Small}
          onClick={() => handleContinue(handleOnSave)}
        >
          {t('administration.blocks.type.multimedia.buttons.accept')}
        </Button>
      </ButtonToolbar>

      <ConfirmationModal
        show={showConfirmationModal}
        title=""
        text={t('administration.modal.fileLoadCancel')}
        acceptButtonText={t('components.modal.buttons.continue')}
        onAccept={confirmModalAction}
        onClose={() => setShowConfirmationModal(false)}
      />
    </Container>
  )
}

export default Multimedia
